import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import {
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../infrastructure/api/default";
import { resolveUploadUrl } from "../../../../infrastructure/utils/resolveUploadUrl";
import FilePreviewLightbox from "./FilePreviewLightbox";
import ImageDownloadButton from "./ImageDownloadButton";

const THUMB_SIZE_DESKTOP = 88;
const THUMB_SIZE_MOBILE = 72;

const isPdfPath = (path) => /\.pdf(\?|$)/i.test(String(path || ""));

const acceptsPdf = (acceptValue) =>
  String(acceptValue || "").toLowerCase().includes("pdf");

const EditableImageField = ({
  label,
  value = "",
  onChange,
  baseUrl = "",
  moduleName = "SLN/CustomerData",
  accept = "image/*",
  disabled = false,
  error = false,
  helperText = "",
  required = false,
  downloadable = false,
  downloadFileName = "",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const thumbSize = isMobile ? THUMB_SIZE_MOBILE : THUMB_SIZE_DESKTOP;
  const inputRef = useRef(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [uploading, setUploading] = useState(false);

  const hasImage = Boolean(value && String(value).trim());
  const isPdf = hasImage && isPdfPath(value);

  useEffect(() => {
    setThumbFailed(false);
  }, [value]);

  const thumbSrc = useMemo(
    () => (hasImage ? resolveUploadUrl(value, baseUrl) : ""),
    [hasImage, value, baseUrl]
  );

  const openZoom = () => {
    if (isPdf && thumbSrc) {
      window.open(thumbSrc, "_blank", "noopener,noreferrer");
      return;
    }
    if (hasImage && !thumbFailed) {
      setPreviewOpen(true);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled) return;

    setUploading(true);
    try {
      const result = await DefaultApiService.uploadFile(
        [{ file, name: file.name }],
        moduleName
      );
      if (result?.isSuccess && result?.data?.length) {
        const uploadedPath = result.data[0]?.directory || result.data[0]?.url;
        if (uploadedPath) {
          onChange?.(uploadedPath);
          setThumbFailed(false);
          toast.success(
            isPdfPath(uploadedPath) || acceptsPdf(accept)
              ? "فایل با موفقیت بارگذاری شد."
              : "تصویر با موفقیت بارگذاری شد."
          );
        } else {
          toast.error("مسیر فایل آپلودشده دریافت نشد.");
        }
      } else {
        toast.error(result?.message || "خطا در آپلود تصویر");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "خطا در آپلود تصویر");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, fontWeight: 500 }}
      >
        {label}
        {required ? (
          <Typography component="span" color="error.main">
            {" "}
            *
          </Typography>
        ) : null}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Paper
          variant="outlined"
          onClick={hasImage && (isPdf || !thumbFailed) ? openZoom : undefined}
          sx={{
            width: thumbSize,
            height: thumbSize,
            overflow: "hidden",
            borderRadius: 1.5,
            borderColor: error ? "error.main" : "divider",
            bgcolor: "grey.50",
            cursor: hasImage && (isPdf || !thumbFailed) ? "zoom-in" : "default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            position: "relative",
          }}
        >
          {uploading ? (
            <CircularProgress size={28} />
          ) : isPdf ? (
            <PictureAsPdfIcon color="error" />
          ) : hasImage && !thumbFailed ? (
            <Box
              component="img"
              src={thumbSrc}
              alt={label}
              loading="lazy"
              decoding="async"
              onError={() => setThumbFailed(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <BrokenImageOutlinedIcon color="disabled" fontSize="small" />
          )}

          {hasImage && (isPdf || !thumbFailed) && !uploading ? (
            <Box
              sx={{
                position: "absolute",
                bottom: 2,
                left: 2,
                bgcolor: "rgba(0,0,0,0.45)",
                borderRadius: 0.5,
                px: 0.5,
                lineHeight: 0,
              }}
            >
              <ZoomInOutlinedIcon sx={{ fontSize: 14, color: "#fff" }} />
            </Box>
          ) : null}
        </Paper>

        {!disabled ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              hidden
              onChange={handleFileSelect}
            />
            <Tooltip title={hasImage ? "جایگزینی تصویر" : "بارگذاری تصویر"}>
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  aria-label={hasImage ? "جایگزینی تصویر" : "بارگذاری تصویر"}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                  {hasImage ? (
                    <UploadFileOutlinedIcon fontSize="small" />
                  ) : (
                    <AddPhotoAlternateOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : hasImage && !thumbFailed ? (
          <Tooltip title="مشاهده بزرگ">
            <IconButton size="small" onClick={openZoom} aria-label="مشاهده بزرگ">
              <ZoomInOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}

        {downloadable && hasImage && !isPdf && !thumbFailed && !uploading ? (
          <ImageDownloadButton src={thumbSrc} fileName={downloadFileName || label} />
        ) : null}
      </Stack>

      {helperText ? (
        <FormHelperText error={error} sx={{ mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      ) : null}

      <FilePreviewLightbox
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        label={label}
        url={value}
        fileBaseUrl={baseUrl}
      />
    </Box>
  );
};

EditableImageField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  baseUrl: PropTypes.string,
  moduleName: PropTypes.string,
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  downloadable: PropTypes.bool,
  downloadFileName: PropTypes.string,
};

export default EditableImageField;
